/**
 * WhatsApp template helpers — parse an approved Meta template into its editable
 * variable slots, and assemble the `components` array the send APIs expect.
 *
 * Meta templates carry variables as `{{1}}` (positional) or `{{first_name}}`
 * (named). The two forms are not interchangeable on the wire: positional params
 * must be sent WITHOUT `parameter_name`, named ones WITH it. Every helper here
 * keys that decision off `token` (the raw text inside the braces) rather than
 * off the FE state key, which is namespaced for uniqueness.
 *
 * Shape of a parsed variable:
 *   {
 *     key,          // unique within its group — safe as a React key / state key
 *     token,        // raw {{...}} contents; decides positional vs named
 *     label,        // human label for the input
 *     example,      // Meta's sample value, used as the input placeholder
 *     buttonIndex?, // BUTTONS only — which button the URL param belongs to
 *     baseUrl?,     // BUTTONS only — static part of the URL, for preview
 *   }
 */

const VAR_RE = /\{\{([^}]+)\}\}/g;

/** Meta's sample value for one variable, across the header/body example shapes. */
function _exampleFor(comp, token) {
  if (comp.example?.body_text_named_params) {
    const hit = comp.example.body_text_named_params.find((p) => p.param_name === token);
    if (hit) return hit.example || "";
  }
  if (comp.example?.header_text_named_params) {
    const hit = comp.example.header_text_named_params.find((p) => p.param_name === token);
    if (hit) return hit.example || "";
  }
  const idx = Number.parseInt(token, 10);
  if (!Number.isNaN(idx) && idx >= 1) {
    if (comp.example?.body_text?.[0]) return comp.example.body_text[0][idx - 1] || "";
    if (comp.example?.header_text) return comp.example.header_text[idx - 1] || "";
  }
  return "";
}

/** Parse the `{{...}}` variables out of a HEADER/BODY component, in order, de-duped. */
export function extractTextParams(comp) {
  if (!comp?.text) return [];
  const params = [];
  const seen = new Set();
  let match;
  VAR_RE.lastIndex = 0;
  while ((match = VAR_RE.exec(comp.text)) !== null) {
    const token = match[1].trim();
    if (seen.has(token)) continue;
    seen.add(token);
    const isPositional = /^\d+$/.test(token);
    params.push({
      key: token,
      token,
      label: isPositional ? `Variable {{${token}}}` : token.replace(/_/g, " "),
      example: _exampleFor(comp, token),
    });
  }
  return params;
}

/** Parse URL-button variables. Only dynamic-URL buttons carry parameters. */
export function extractButtonParams(comp) {
  if (comp?.type !== "BUTTONS" || !Array.isArray(comp.buttons)) return [];
  const params = [];
  comp.buttons.forEach((btn, i) => {
    if (btn.type !== "URL" || !btn.url) return;
    // The static part of the URL — the variable is always a suffix.
    const baseUrl = btn.url.replace(/\{\{\d+\}\}/g, "").replace(/\/$/, "");
    const re = /\{\{(\d+)\}\}/g;
    let match;
    while ((match = re.exec(btn.url)) !== null) {
      const token = match[1].trim();
      params.push({
        // Namespaced so two buttons' {{1}} don't collide in component state.
        key: `btn_${i}_${token}`,
        token,
        label: `Button "${btn.text}" URL suffix`,
        example: btn.example?.[0] || "",
        buttonIndex: i,
        baseUrl,
      });
    }
  });
  return params;
}

/** Split a template into its header / body / button variable slots. */
export function extractTemplateVars(template) {
  const empty = { header: [], body: [], button: [] };
  if (!template?.components) return empty;
  const byType = (t) => template.components.find(
    (c) => String(c?.type || "").toUpperCase() === t,
  );
  const header = byType("HEADER");
  // Media headers (IMAGE/VIDEO/DOCUMENT) have no text variables to fill.
  const isTextHeader = String(header?.format || "TEXT").toUpperCase() === "TEXT";
  return {
    header: isTextHeader ? extractTextParams(header) : [],
    body: extractTextParams(byType("BODY")),
    button: extractButtonParams(byType("BUTTONS")),
  };
}

export function countTemplateVars(vars) {
  return vars.header.length + vars.body.length + vars.button.length;
}

/**
 * Assemble the Meta `components` array from filled-in values.
 *
 * @param vars   output of extractTemplateVars
 * @param values flat map keyed `${group}_${param.key}` (e.g. "body_1", "button_btn_0_1")
 */
export function buildComponents(vars, values) {
  const components = [];
  const mkParam = (p, group) => {
    const param = { type: "text", text: values[`${group}_${p.key}`] || "" };
    // Named variables carry parameter_name; positional ones must not.
    if (!/^\d+$/.test(p.token)) param.parameter_name = p.token;
    return param;
  };

  if (vars.header.length) {
    components.push({ type: "header", parameters: vars.header.map((p) => mkParam(p, "header")) });
  }
  if (vars.body.length) {
    components.push({ type: "body", parameters: vars.body.map((p) => mkParam(p, "body")) });
  }
  if (vars.button.length) {
    const byBtn = {};
    vars.button.forEach((p) => {
      const idx = p.buttonIndex ?? 0;
      if (!byBtn[idx]) byBtn[idx] = [];
      byBtn[idx].push(p);
    });
    Object.entries(byBtn).forEach(([idx, params]) => {
      components.push({
        type: "button",
        sub_type: "url",
        index: Number(idx),
        parameters: params.map((p) => mkParam(p, "button")),
      });
    });
  }
  return components;
}

/** Substitute values into a component's text for preview. Unfilled slots stay visible. */
export function fillPreviewText(text, params, values, group) {
  if (!text) return text;
  return params.reduce((acc, p) => {
    const val = values[`${group}_${p.key}`];
    return acc.replaceAll(`{{${p.token}}}`, val || `{{${p.token}}}`);
  }, text);
}

/** Pull the plain text of a template part, for previews and summaries. */
export function templateText(template, type) {
  const comp = (template?.components || []).find(
    (c) => String(c?.type || "").toUpperCase() === type,
  );
  return comp?.text || "";
}
